import { animated, config, useSpring } from '@react-spring/web';
import clsx from 'clsx';
import DOMPurify from 'dompurify';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactSwitch from 'react-switch';

import {
    CookieCategory,
    CookieCategoryType,
    CookieConfig,
    CookieDetail,
    CookieType,
} from '../../api/types/cookies';
import CategoryTab from './CategoryTab';

interface CookieModalProps {
    data: CookieConfig['config'];
    allow: (categories?: string[]) => void;
}

function CookieModal({ data, allow }: CookieModalProps) {
    const { t, i18n } = useTranslation('global', { keyPrefix: 'cookies' });

    const [tab, setTab] = useState(1);
    const [categoryOpen, setCategoryOpen] = useState<string>('');
    const [providerOpen, setProviderOpen] = useState<string>('');
    const [consent, setConsent] = useState<string[]>([]);

    const styles = useSpring({
        from: {
            opacity: 0,
            marginTop: 40,
        },
        to: {
            opacity: 1,
            marginTop: 0,
        },
        config: { ...config.default, duration: 100 },
    });

    const toggleCategory = (id: string) => {
        setCategoryOpen(categoryOpen === id ? '' : id);
    };

    const toggleProvider = (id: string) => {
        setProviderOpen(providerOpen === id ? '' : id);
    };

    const handleConsent = (id: string, checked: boolean) => {
        const i = consent.indexOf(id);
        if (i === -1 && checked) setConsent([...consent, id]);
        else if (!checked)
            setConsent([...consent.slice(0, i), ...consent.slice(i + 1)]);
    };

    if (!data || !i18n || !i18n.resolvedLanguage) return null;

    const lang = i18n.resolvedLanguage || 'en';
    const categories = data.categories.map((category: CookieCategory) => {
        const tpe = data.categoryTypes.find(
            (t: CookieCategoryType) => t.id === category.categoryTypeFk.id,
        );
        if (!tpe.active) return null;

        const count = category.providers.reduce(
            (p, c) => p + c.details.length,
            0,
        );
        return tpe && tpe[lang] ? (
            <div key={tpe.id}>
                <ReactSwitch
                    onChange={(checked) =>
                        handleConsent(category.categoryTypeFk.id, checked)
                    }
                    checked={
                        tpe.necessary ||
                        consent.includes(category.categoryTypeFk.id)
                    }
                    disabled={tpe.necessary}
                    checkedIcon={false}
                    uncheckedIcon={false}
                    className="float-right"
                />

                <h3 className="mt-4 text-xl">
                    {tpe[lang].name}
                    <span className="ml-2 rounded-full bg-light px-2 py-1 text-sm text-black">
                        {count}
                    </span>
                </h3>

                <div
                    onClick={() => toggleCategory(tpe.id)}
                    className="mt-5 cursor-pointer normal-case text-dark transition-all hover:text-black"
                    dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(tpe[lang].description),
                    }}
                />
                <div
                    className={clsx(
                        'mt-4 max-h-0 overflow-hidden border border-dark pb-4 opacity-0 transition-all',
                        {
                            'max-h-[10000px] opacity-100':
                                categoryOpen.includes(tpe.id),
                        },
                    )}>
                    {category.providers.map((provider) => (
                        <div
                            key={`provider-${provider.id}`}
                            className="px-4 py-2">
                            <button
                                type="button"
                                className="my-4 block text-xl uppercase text-dark hover:text-black"
                                onClick={() => toggleProvider(provider.id)}>
                                {provider[lang].name}
                                <span className="ml-2 rounded-full bg-light px-2 py-1 text-sm text-black">
                                    {provider.details.length}
                                </span>
                            </button>
                            <a
                                target="_blank"
                                rel="noopener noreferrer"
                                href={provider[lang].link}
                                className="btn-hover text-xxl pb-1 uppercase leading-10">
                                <span className="hover-right inline-block pr-4 text-xl">
                                    →
                                </span>
                                <span>{t('moreOnProvider')}</span>
                            </a>
                            <div
                                className={clsx(
                                    'max-h-0 overflow-hidden opacity-0 transition-all',
                                    {
                                        'max-h-[1000px] opacity-100':
                                            providerOpen.includes(provider.id),
                                    },
                                )}>
                                <div className="mt-6">
                                    {provider.details.map(
                                        (cookie: CookieDetail) => {
                                            const cookieType = data.types.find(
                                                (t: CookieType) =>
                                                    t.id === cookie.typeFk.id,
                                            );
                                            return (
                                                <div
                                                    key={`cookie-${cookie.id}`}
                                                    className="border-1 mb-4 border bg-light px-4 py-4 normal-case">
                                                    <h5 className="mb-2">
                                                        {cookie[lang].name}
                                                    </h5>
                                                    <div
                                                        className="border-1 border-b"
                                                        dangerouslySetInnerHTML={{
                                                            __html: DOMPurify.sanitize(
                                                                cookie[lang]
                                                                    .description,
                                                            ),
                                                        }}
                                                    />
                                                    <div className="flex pt-4">
                                                        <span className="w-1/2">
                                                            {t('validity')}:{' '}
                                                            {
                                                                cookie[lang]
                                                                    .validity
                                                            }
                                                        </span>
                                                        <span className="w-1/2">
                                                            {t('type')}:{' '}
                                                            {
                                                                cookieType[lang]
                                                                    .name
                                                            }
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        },
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        ) : null;
    });

    return (
        <div
            className="align-center fixed left-0 top-0 flex h-screen w-full flex-col overflow-auto sm:justify-center"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}>
            <div />

            <animated.div
                style={styles}
                className="z-20 mx-auto min-w-[75%] max-w-[900px] overflow-auto bg-white px-6 py-4">
                <div className="border-gray border-b py-4 text-xl sm:text-2xl">
                    {t('title')}
                </div>
                <div className="flex flex-row py-2">
                    <CategoryTab
                        index={0}
                        label={t('consent')}
                        onClick={setTab}
                        active={tab}
                    />
                    <CategoryTab
                        index={1}
                        label={t('details')}
                        onClick={setTab}
                        active={tab}
                    />
                    <CategoryTab
                        index={2}
                        label={t('about')}
                        onClick={setTab}
                        active={tab}
                    />
                </div>
                {tab === 0 && (
                    <div
                        className="py-4 normal-case"
                        dangerouslySetInnerHTML={{
                            __html: data[i18n.resolvedLanguage].consent,
                        }}></div>
                )}
                {tab === 1 && <div>{categories}</div>}
                {tab === 2 && (
                    <div
                        className="py-4 normal-case"
                        dangerouslySetInnerHTML={{
                            __html: data[i18n.resolvedLanguage].about,
                        }}></div>
                )}

                <div className="flex flex-col lg:flex-row">
                    <div className="text-center">
                        <button
                            type="button"
                            onClick={() =>
                                allow(
                                    data.categoryTypes
                                        .filter(
                                            (cT: CookieCategoryType) =>
                                                cT.active,
                                        )
                                        .map((cT: CookieCategoryType) => cT.id),
                                )
                            }
                            className="font-xl btn-hover mr-6 uppercase tracking-[3.8px]">
                            <span className="hover-right inline-block pr-4 text-xl">
                                →
                            </span>
                            <span>{t('allowAll')}</span>
                        </button>
                    </div>

                    <div className="text-center">
                        <button
                            type="button"
                            onClick={() => {
                                allow();
                            }}
                            className="font-xl btn-hover pr-4 uppercase tracking-[3.8px]">
                            <span className="hover-right inline-block pr-4 text-xl">
                                →
                            </span>
                            <span>{t('disallowAll')}</span>
                        </button>
                    </div>

                    <div className="lg:align-right text-center lg:grow">
                        <button
                            type="button"
                            className="font-xl btn-hover pr-4 uppercase tracking-[3.8px] lg:float-right"
                            onClick={() => allow(consent)}>
                            <span>{t('setup')}</span>
                            <span className="hover-down inline-block pl-4 text-xl">
                                ↓
                            </span>
                        </button>
                    </div>
                </div>
            </animated.div>
        </div>
    );
}

export default CookieModal;
